import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';

import { SignalRService } from './signalr.service';
import { CrudService } from './crud.service';
import {
  HUB_EVENTS,
  RaceRequestStatusPayload,
} from 'src/models/signalr-events.model';
import {
  EnterInLineViewModel,
  RaceRequestViewModel,
} from 'src/models/user.model';

/**
 * CallerFacadeService — Phase 2.6
 *
 * Orchestrates the caller/seller trip-request lifecycle:
 *  • Wires hub events from SignalRService.events$ into typed Observables
 *  • Exposes typed Observables for events the call page must react to
 *  • Provides action methods wrapping all SignalR invocations the page uses
 *  • Provides HTTP data methods (CrudService calls from the page)
 */
@Injectable({ providedIn: 'root' })
export class CallerFacadeService implements OnDestroy {

  // ── Hub event Observables (page subscribes to these) ─────────────────────

  /**
   * Emits race-request status updates from the server.
   * Each payload contains { status, race, raceId }.
   */
  readonly raceRequestStatus$: Observable<RaceRequestStatusPayload> =
    this._signalR.events$.pipe(
      filter(e => e.type === HUB_EVENTS.RACE_REQUEST_STATUS),
      map(e => e.payload as RaceRequestStatusPayload),
      share(),
    );

  /**
   * Emits when the server notifies the caller that a driver cancelled the trip.
   */
  readonly tripCancelled$: Observable<any> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.CANCEL_TRIP),
    map(e => e.payload),
    share(),
  );

  /**
   * Emits the seller connection status string from the hub.
   */
  readonly sellerStatus$: Observable<string> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.SELLER_STATUS),
    map(e => e.payload as string),
    share(),
  );

  /**
   * Emits when the hub sends the current list of online drivers.
   */
  readonly driversOnline$: Observable<any[]> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.DRIVERS_ONLINE),
    map(e => e.payload as any[]),
    share(),
  );

  /**
   * Emits on verify-connection server ping.
   */
  readonly connectionVerified$: Observable<boolean> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.VERIFY_CONNECTION),
    map(e => e.payload as boolean),
    share(),
  );

  /**
   * Emits when a driver accepts a race that was dispatched by this caller/driver.
   * Used for the driver-as-caller flow (OnAcceptOwnRace).
   */
  readonly driverAcceptedOwnRace$: Observable<any> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.ON_ACCEPT_OWN_RACE),
    map(e => e.payload),
    share(),
  );

  /**
   * Emits when a driver accepts a tour that was dispatched by this caller/driver.
   * Used for the driver-as-caller flow (OnAcceptOwnTour).
   */
  readonly driverAcceptedOwnTour$: Observable<any> = this._signalR.events$.pipe(
    filter(e => e.type === HUB_EVENTS.ON_ACCEPT_OWN_TOUR),
    map(e => e.payload),
    share(),
  );

  private _subs = new Subscription();

  constructor(
    private _signalR: SignalRService,
    private _crud: CrudService,
  ) {}

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  // ── SignalR connection lifecycle ──────────────────────────────────────────

  /**
   * Tears down any existing hub connection and creates a fresh one.
   */
  async setupConnection(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.disconnect();
    this._signalR.clearHub();
    await this._signalR.setUpConnection(viewModel);
  }

  /**
   * Registers the seller on the hub so the server can route race-status events
   * back to this connection.
   */
  async connectSeller(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setConnectSeller(viewModel);
  }

  /**
   * Temporarily disconnects the hub (stops the connection without clearing it).
   * Used when the caller page is leaving.
   */
  async tempDisconnect(): Promise<void> {
    await this._signalR.tempDisconnectSeller();
  }

  // ── Race/tour request actions ─────────────────────────────────────────────

  /**
   * Sends a new race request from a caller/seller.
   */
  async newRaceRequest(raceRequest: RaceRequestViewModel): Promise<void> {
    await this._signalR.setNewRaceRequest(raceRequest);
  }

  /**
   * Sends a new race request initiated by a driver on behalf of a passenger.
   */
  async newRaceRequestDriver(raceRequest: RaceRequestViewModel): Promise<void> {
    await this._signalR.setNewRaceRequestDriver(raceRequest);
  }

  /**
   * Sends a new tour/package request from a caller/seller.
   */
  async newTourRequest(raceRequest: RaceRequestViewModel): Promise<void> {
    await this._signalR.setNewTourRequest(raceRequest);
  }

  /**
   * Sends a new tour/package request initiated by a driver.
   */
  async newTourRequestDriver(raceRequest: RaceRequestViewModel): Promise<void> {
    await this._signalR.setNewTourRequestDriver(raceRequest);
  }

  /**
   * Cancels a pending race request by ID.
   */
  async cancelRaceRequest(raceId: string): Promise<void> {
    await this._signalR.setCancelRaceRequest(raceId);
  }

  /**
   * Refuses a race on behalf of the caller (auto-refuse after countdown).
   */
  async refuseRace(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setRefuseRace(viewModel);
  }

  /**
   * Leaves the driver queue (driver-as-caller flow).
   */
  async leaveLine(viewModel: EnterInLineViewModel): Promise<void> {
    await this._signalR.setLeaveLine(viewModel);
  }

  // ── HTTP data methods ─────────────────────────────────────────────────────

  /**
   * Fetches all fixed destinations for a given type (tour=0, package=1).
   */
  getFixedDestinations(destinationType: number): Observable<any> {
    return this._crud.getData(
      `destino/todosPorTipo?destinationType=${destinationType}`,
    );
  }

  /**
   * Calculates the price for a trip given origin/destination and trip parameters.
   */
  getRacePrice(checkPriceViewModel: any): Observable<any> {
    return this._crud.postData(
      'corrida/preco-corrida',
      null,
      checkPriceViewModel,
    );
  }

  /**
   * Validates whether a coupon code is applicable.
   */
  validateCoupon(coupon: string): Observable<boolean> {
    return this._crud.getData(
      `Corrida/cupom-valido?coupon=${coupon}`,
    ) as Observable<boolean>;
  }

  /**
   * Cold-start ping for the seller/caller — fire-and-forget.
   */
  coldStartSeller(): Observable<any> {
    return this._crud.postData('vendedor/cold-start', null, null);
  }

  /**
   * Sends an SMS notification after a driver accepts a race/tour.
   */
  sendSms(body: { numeroDestinatario: string; mensagem: string }): Observable<any> {
    return this._crud.postData('sms/send-sms', null, body);
  }

  /**
   * Checks whether the driver has an upcoming scheduled race soon.
   * Returns true if a race is scheduled within the relevant time window.
   */
  hasUpcomingScheduledRace(): Observable<boolean> {
    return this._crud.getData('Corrida/corrida-agendada-breve') as Observable<boolean>;
  }
}
